import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import {
  Map,
  MapWithState,
  MapSettings,
  TemporaryFolderUIds,
  Folder,
  Layer,
  MapLink,
  MapLayerView,
  FolderVisibility,
  SectionType
} from './models';
import * as copActions from './cop.actions';
import produce from 'immer';
import { GUID_EMPTY } from '@app/core/constants';
import { RsdState } from '../rsd/rsd.model';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { SelectedMapState } from './models/map-state.model';
import { MapLayerSummary } from '../lsd/models/map-layer-summary.model';
import { Share } from 'src/app/modules/core/store/models/content-share.model';
import { ListsState } from '../lists';
import { ImpactedAssetsState } from '../impacted-assets';
import { MapPosition } from '../../drawing/models/map-position.model';
import { CamerasState } from '../cameras';
import { WeatherFolder } from '@app/features/operating-picture/xweather/models/weather-folder.model';

export const copModuleKey = 'cop';
export const copFeatureKey = 'cop';

export interface CopModuleState {
  cop: CopState;
  rsd: RsdState;
  lists: ListsState;
  impactedAssets: ImpactedAssetsState;
  cameras: CamerasState;
}
export interface CopState {
  maps: Map[];
  currentMap?: MapWithState;
  mapCopy?: MapWithState;
  mapSettings?: MapSettings;
  mapPosition?: MapPosition;
  mapSettingsFromStorageToLoad?: {
    mapSettings: MapSettings;
    selectedMapUId: string;
  };
  selectedMapState: SelectedMapState;
  previouslySelectedMapUId?: string;
  mapLinks?: MapLink[];
  mapLayerViews?: MapLayerView[];
  mapRoomCollaborators?: Share[];
  selectedMapUIdForDelete?: string;
  linkDocumentUId?: string;
  linkDocumentDatasetUId?: string;
  floorValues: string[];
  selectedFloor: string;
  isMapLoading: boolean;
  ui: {
    isDirty: boolean;
    isMapEdit: boolean;
    isMapNew: boolean;
    isMapWallEdit: boolean;
    showMapRoomDialog: boolean;
    showNightLayer: boolean;
    isOverviewMode: boolean;

    // LSD
    isTrySaveMap?: boolean;
    isFolderEdit?: boolean;
    isLayerEdit?: boolean;
    isLayerNew?: boolean;
    isLayerEditQuickFilter?: boolean;
    isFolderWorkflowSummaryConfigEdit?: boolean;
    showLayerOptionsTab?: string;
  };

  historyTrackEnabledUIds: Array<string>;

  // LSD
  datasetsForSelect?: Dataset[];
  selectedFolder: Folder;
  selectedFolderForDelete: Folder;
  selectedLayer: Layer;
  selectedLayerForDelete: Layer;
  workflowStatusSummary: MapLayerSummary[];
}

export const initialCopState: CopState = {
  maps: [],
  currentMap: null,
  mapCopy: null,
  mapSettings: {
    tropicalCycloneExpanded: {},
    folderExpanded: {},
    folderVisibility: {},
    layerVisibility: {},
    layerOpacity: {},
    layerQuickFilterSelections: {},
    workflowStatusVisibilityByLayerTypeAndName: {},
    workflowStatusExpandedByLayerTypeAndName: {},
    geoFieldsForOptionalDisplay: [],
    sectionWidgetUserSettings: {},
    hiddenDocumentsByLayer: {},
    mapPosition: {
      zoomLevel: 3,
      northEast: { lat: 71.55274065141299, lng: 122.6953125 },
      southWest: { lat: -63.58767529470318, lng: 172.6171875 },
      center: { lat: 10.487811882056695, lng: -32.34375 }
    },
    isLeftDrawerOpen: true,
    mapTypeSettings: {
      selectedMapType: null,
      isTraffic: false,
      isTransit: false,
      isPOI: false,
      isLabled: true
    }
  },
  floorValues: [],
  selectedFloor: '1',
  isMapLoading: false,
  mapSettingsFromStorageToLoad: null,
  selectedMapState: {
    mapTileLayers: [],
    calculatedLayerFilterOptions: []
  },
  previouslySelectedMapUId: null,
  mapLinks: [],
  mapLayerViews: [],
  mapRoomCollaborators: [],
  selectedMapUIdForDelete: null,
  linkDocumentUId: null,
  linkDocumentDatasetUId: null,

  ui: {
    isDirty: false,
    isMapEdit: false,
    isMapNew: false,
    isMapWallEdit: false,
    showMapRoomDialog: false,
    showNightLayer: false,
    isOverviewMode: false,

    // LSD
    isTrySaveMap: false,
    isFolderEdit: false,
    isLayerEdit: false,
    isLayerNew: false,
    isLayerEditQuickFilter: false,
    isFolderWorkflowSummaryConfigEdit: false,
    showLayerOptionsTab: null
  },

  historyTrackEnabledUIds: [],

  // LSD
  datasetsForSelect: [],
  selectedFolder: null,
  selectedFolderForDelete: null,
  selectedLayer: null,
  selectedLayerForDelete: null,
  workflowStatusSummary: []
};

export const reducer: ActionReducer<CopState, Action> = createReducer(
  initialCopState,
  on(copActions.loadMapsForUserCompleteToStore, (state, action) => {
    return {
      ...state,
      maps: action.maps
    };
  }),
  on(copActions.removeMapFromStore, (state, action) => ({
    ...state,
    maps: state.maps.filter(m => m.uId !== action.mapUId)
  })),
  on(copActions.loadMapWithStateCompleteToStore, (state, action) => {
    return {
      ...state,
      currentMap: action.mapWithState
    };
  }),
  on(copActions.loadDatasetsForSelectedMapCompleteToStore, (state, action) => {
    const { datasets } = action;
    const datasetsSet = {};

    datasets.map(ds => (datasetsSet[ds.uId] = ds));

    return {
      ...state,
      currentMap: {
        ...state.currentMap,
        datasetsSet
      },
      floorValues: []
    };
  }),
  on(copActions.setFloorsForSelectedMap, (state, { floorValues = [] }) => ({
    ...state,
    floorValues
  })),
  on(copActions.setLayersLoading, (state, { layerUIds = [] }) => {
    if (!state?.currentMap?.layerStates) {
      return state;
    }

    const layerStates = Object.entries(state.currentMap.layerStates).reduce(
      (acc, [layerUId, layerState]) => ({
        ...acc,
        [layerUId]: {
          ...layerState,
          isLoading: layerUIds.includes(layerUId)
        }
      }),
      {} as MapWithState['layerStates']
    );

    return {
      ...state,
      currentMap: {
        ...state.currentMap,
        layerStates
      }
    };
  }),
  on(copActions.setLayerLoadingOnToStore, (state, action) => {
    const { layerUId } = action;

    return produce(state, draft => {
      if (!!draft.currentMap?.layerStates[layerUId]) {
        draft.currentMap.layerStates[layerUId].isLoading = true;
      }
    });
  }),
  on(copActions.setLayerLoadingOffToStore, (state, action) => {
    const { layerUId } = action;

    return produce(state, draft => {
      if (!!draft.currentMap) {
        if (!!!draft.currentMap.layerStates[layerUId]) {
          draft.currentMap.layerStates[layerUId] = {};
        }
        draft.currentMap.layerStates[layerUId].isLoading = false;
        if (action.isError === true) {
          draft.currentMap.layerStates[layerUId].isError = true;
        }
      }
    });
  }),
  on(copActions.setUserMapSettingsToLoadToStore, (state, action) => {
    return {
      ...state,
      mapSettingsFromStorageToLoad: { ...action },
      mapSettings: {
        ...state.mapSettings,
        ...action.mapSettings
      }
    };
  }),
  on(copActions.setIsMaploadingToStore, (state, action) => {
    return {
      ...state,
      isMapLoading: action.loading
    };
  }),
  on(copActions.setUiStateFromStoredSettingsToStore, (state, action) => {
    if (!state.mapSettingsFromStorageToLoad) {
      return state;
    }

    return {
      ...state,
      settingsFromStorageToLoad: null,
      ui: {
        ...state.ui
      },
      mapSettings: {
        ...state.mapSettings,
        ...state.mapSettingsFromStorageToLoad.mapSettings
      }
    };
  }),

  on(copActions.editSelectedMapToStore, (state, action) => {
    const maps = state.maps.slice();
    let mapBeingModified = state.currentMap;

    // Remove any temporary folders before editing
    if (mapBeingModified.map.folders[0]?.folders?.length > 0) {
      const tempFolderIds = Object.values(TemporaryFolderUIds);
      const rootFolder = { ...mapBeingModified.map.folders[0] };
      rootFolder.folders = rootFolder.folders.filter(f => !tempFolderIds.find(tf => tf === f.uId));
      mapBeingModified = {
        ...mapBeingModified,
        map: {
          ...mapBeingModified.map,
          folders: [rootFolder]
        }
      };

      // Also update the saved maps to reflect this
      const indexToRemove = maps.findIndex(m => m.uId === mapBeingModified.map.uId);
      if (indexToRemove >= 0) {
        maps.splice(indexToRemove, 1, ...[mapBeingModified.map]);
      }
    }

    // If bad map model condition, fix it
    // 1. Stray folders with no parent ID
    if (mapBeingModified.map.folders.filter(f => !f.parentUId).length > 1) {
      const maps = state.maps.slice();
      const index = maps.findIndex(m => m.uId === mapBeingModified.map.uId);
      if (index >= 0) {
        const modifiedRootFolder = { ...mapBeingModified.map.folders[0] };
        modifiedRootFolder.folders = modifiedRootFolder.folders.concat(
          mapBeingModified.map.folders.slice(1).map(f => {
            return { ...f, parentUId: modifiedRootFolder.uId };
          })
        );

        const fixedMap = {
          ...mapBeingModified,
          map: {
            ...mapBeingModified.map,
            folders: [modifiedRootFolder]
          }
        };
        maps.splice(index, 1, ...[fixedMap.map]);

        return {
          ...state,
          maps,
          mapCopy: mapBeingModified,
          ui: {
            ...state.ui,
            isMapEdit: true,
            isMapWallEdit: false,
            isDirty: true
          }
        };
      }
    }

    return {
      ...state,
      maps: maps,
      mapCopy: mapBeingModified,
      ui: {
        ...state.ui,
        isMapEdit: true,
        isMapWallEdit: false
      }
    };
  }),

  on(copActions.editMapWithStateCompleteToStore, (state, action) => {
    const mapBeingModified = action.mapWithState;

    // If bad map model condition, fix it
    // 1. Stray folders with no parent ID
    if (mapBeingModified.map.folders.filter(f => !f.parentUId).length > 1) {
      const maps = state.maps.slice();
      const index = maps.findIndex(m => m.uId === mapBeingModified.map.uId);
      if (index >= 0) {
        const modifiedRootFolder = { ...mapBeingModified.map.folders[0] };
        modifiedRootFolder.folders = modifiedRootFolder.folders.concat(
          mapBeingModified.map.folders.slice(1).map(f => {
            return { ...f, parentUId: modifiedRootFolder.uId };
          })
        );

        const fixedMap = {
          ...mapBeingModified,
          map: {
            ...mapBeingModified.map,
            folders: [modifiedRootFolder]
          }
        };
        maps.splice(index, 1, ...[fixedMap.map]);

        return {
          ...state,
          maps,
          mapCopy: mapBeingModified,
          ui: {
            ...state.ui,
            isMapEdit: true,
            isMapWallEdit: false,
            isDirty: true
          }
        };
      }
    }

    return {
      ...state,
      mapCopy: mapBeingModified,
      ui: {
        ...state.ui,
        isMapEdit: true,
        isMapWallEdit: false
      }
    };
  }),

  on(copActions.setCancelEditMapToStore, (state, action) => {
    const mapBackup = state.mapCopy;
    const mapUId = mapBackup?.map.uId || GUID_EMPTY;

    return {
      ...state,
      mapCopy: null,
      currentMap: mapBackup,
      ui: {
        ...state.ui,
        isMapEdit: false,
        isMapWallEdit: false,
        isDirty: false
      }
    };
  }),

  on(copActions.removeUnsavedMapAfterCancel, (state, action) => {
    const maps = state.maps;
    var newMaps = maps.filter(m => m.uId !== GUID_EMPTY);

    return {
      ...state,
      maps: newMaps
    };
  }),

  on(copActions.deleteSelectedMapCompleteToStore, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isMapEdit: false
      }
    };
  }),
  on(copActions.addSelectedDocumentToStore, (state, action) => {
    const { document, clearExisting } = action;

    const selectedDocuments = clearExisting
      ? {}
      : {
          ...state.currentMap.selectedDocuments
        };
    selectedDocuments[document.uid] = document;

    return {
      ...state,
      currentMap: {
        ...state.currentMap,
        selectedDocuments
      }
    };
  }),
  on(copActions.removeSelectedDocumentToStore, (state, action) => {
    const { document } = action;

    const selectedDocuments = {
      ...state.currentMap.selectedDocuments
    };
    if (!!selectedDocuments[document.uid]) {
      delete selectedDocuments[document.uid];
    }

    return {
      ...state,
      currentMap: {
        ...state.currentMap,
        selectedDocuments
      }
    };
  }),
  on(copActions.clearSelectedDocumentsToStore, (state, action) => {
    const selectedDocuments = {};

    return {
      ...state,
      currentMap: {
        ...state.currentMap,
        selectedDocuments
      }
    };
  }),
  on(copActions.refreshMapRoomCompleteToStore, (state, action) => {
    var maps = action?.results?.data?.slice();
    var selectedMap = JSON.parse(JSON.stringify(maps.find(m => m.uId === action.selectedMapUId)));
    var selectedMapFolders = state.maps.find(m => m.uId === action.selectedMapUId)?.folders;

    selectedMap.folders = selectedMapFolders.slice();

    var loadedMaps = maps.filter(m => m.uId !== action.selectedMapUId)?.slice();
    loadedMaps.push(selectedMap);

    return {
      ...state,
      maps: loadedMaps
    };
  }),

  on(copActions.cancelNewMapToStore, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isMapNew: false
      }
    };
  }),
  on(copActions.showNewMapToStore, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isMapNew: true,
        isMapWallEdit: false
      }
    };
  }),
  on(copActions.newMapCompleteToStore, (state, action) => {
    const { newMapState } = action;

    const maps = state.maps.slice();
    maps.push(newMapState.map);

    const previouslySelectedMapUId = state.currentMap?.map?.uId;

    return {
      ...state,
      maps,
      currentMap: newMapState,
      previouslySelectedMapUId: previouslySelectedMapUId,
      mapCopy: null,
      ui: {
        ...state.ui,
        isMapNew: false,
        isMapEdit: true,
        isMapWallEdit: false,
        isDirty: true
      }
    };
  }),
  on(copActions.storeCreatedMapToStore, (state, action) => {
    const { mapState } = action;
    const maps = state.maps?.slice() ?? [];
    const indexToRemove = maps.findIndex(m => m.uId === GUID_EMPTY);
    if (indexToRemove >= 0) {
      maps.splice(indexToRemove, 1);
    }
    maps.push(mapState.map);

    return {
      ...state,
      mapCopy: null,
      maps,
      currentMap: mapState,
      /*      selectedMapState: {
        ...state.selectedMapState,
        map: map
      },*/
      ui: {
        ...state.ui,
        isMapEdit: action.cancelEdit ? false : state.ui.isMapEdit,
        isMapWallEdit: action.cancelEdit ? false : state.ui.isMapWallEdit,
        isDirty: false
      }
    };
  }),
  on(copActions.updateMapCompleteToStore, (state, action) => {
    const { mapState } = action;

    const maps = state.maps.slice();
    const indexToRemove = maps.findIndex(m => m.uId === mapState.map.uId);
    if (indexToRemove >= 0) {
      maps.splice(indexToRemove, 1, ...[mapState.map]);
    }

    return {
      ...state,
      mapCopy: null,
      maps,
      currentMap: mapState,
      /*      selectedMapState: {
        ...state.selectedMapState,
        map: map
      },*/
      ui: {
        ...state.ui,
        isMapEdit: action.cancelEdit ? false : state.ui.isMapEdit,
        isMapWallEdit: action.cancelEdit ? false : state.ui.isMapWallEdit,
        isDirty: false
      }
    };
  }),
  on(copActions.setMapDirtyToStore, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isDirty: true
      }
    };
  }),
  on(copActions.setMapForDeleteToStore, (state, action) => {
    return {
      ...state,
      selectedMapUIdForDelete: action.uId
    };
  }),
  on(copActions.clearMapForDeleteToStore, (state, action) => {
    return {
      ...state,
      selectedMapUIdForDelete: null
    };
  }),
  on(copActions.deleteSelectedMapCompleteToStore, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isMapEdit: false
      }
    };
  }),

  on(copActions.showMapRoomToStore, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showMapRoomDialog: true
      }
    };
  }),
  on(copActions.hideMapRoomToStore, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showMapRoomDialog: false
      }
    };
  }),
  on(copActions.getMapRoomCollaboratorsCompleteToStore, (state, action) => {
    return {
      ...state,
      mapRoomCollaborators: action.results
    };
  }),
  on(copActions.queryForMapLinksCompleteToStore, (state, action) => {
    const mapLinks = action?.results?.data;

    return {
      ...state,
      mapLinks: mapLinks
    };
  }),
  on(copActions.shareMapLinkCompleteToStore, (state, action) => {
    var newMapLink = [...state.mapLinks];

    newMapLink.push(action.mapLink);

    return {
      ...state,
      mapLinks: newMapLink
    };
  }),
  on(copActions.deleteMapLinkCompleteToStore, (state, action) => {
    var newMapLinks = [...state.mapLinks];

    const index = state.mapLinks.findIndex(d => d.uId === action.uId);
    if (index >= 0) {
      newMapLinks.splice(index, 1);
    }

    return {
      ...state,
      mapLinks: newMapLinks
    };
  }),
  // TODO: Check this
  on(copActions.setTimedTriggersByLayerAndTypeToStore, (state, action) => {
    return {
      ...state,
      timedTriggersByLayerAndType: action.triggers
    };
  }),
  on(copActions.getMapLayerViewFromApiCompleteToStore, (state, action) => {
    return {
      ...state,
      mapLayerViews: action.results
    };
  }),

  //
  // Left Drawer
  //
  on(copActions.setCalculateLayerFilterOptionsToStore, (state, action) => {
    return {
      ...state,
      selectedMapState: {
        ...state.selectedMapState,
        calculatedLayerFilterOptions: action.layerFilterOptions
      }
    };
  }),
  on(copActions.setMapTileLayersToStore, (state, action) => {
    return {
      ...state,
      selectedMapState: {
        ...state.selectedMapState,
        mapTileLayers: action.tileLayers
      }
    };
  }),

  on(copActions.storeNewFolderAdditionToStore, (state, action) => {
    const { folder, mapState } = action;

    const maps = state.maps.slice();
    const index = maps.findIndex(m => m.uId === mapState.map.uId);
    if (index >= 0) {
      maps.splice(index, 1, ...[mapState.map]);
    }

    return {
      ...state,
      selectedFolder: folder,
      selectedMapUId: mapState.map.uId,
      maps,
      currentMap: mapState,
      ui: {
        ...state.ui,
        isFolderEdit: true,
        isDirty: true
      }
    };
  }),
  on(copActions.editFolderToStore, (state, action) => {
    return {
      ...state,
      selectedFolder: action.folder,
      ui: {
        ...state.ui,
        isFolderEdit: true,
        isFolderWorkflowSummaryConfigEdit: action.isFolderWorkflowSummaryConfigEdit
      }
    };
  }),
  on(copActions.cancelEditFolderToStore, (state, action) => {
    return {
      ...state,
      selectedFolder: null,
      ui: {
        ...state?.ui,
        isFolderEdit: false,
        isFolderWorkflowSummaryConfigEdit: false
      }
    };
  }),
  on(copActions.setTrySaveMapToStore, (state, action) => {
    return {
      ...state,
      ui: {
        ...state?.ui,
        isTrySaveMap: action.isTrySaveMap
      }
    };
  }),
  on(copActions.storeFolderDeleteToStore, (state, action) => {
    return {
      ...state,
      selectedFolderForDelete: action.folder
    };
  }),
  on(copActions.setDraftMapToStore, (state, action) => {
    const { mapState } = action;

    const maps = state.maps.slice();
    const index = maps.findIndex(m => m.uId === mapState.map.uId);
    if (index >= 0) {
      maps.splice(index, 1, ...[mapState.map]);
    }

    return {
      ...state,
      maps,
      currentMap: mapState,
      selectedFolder: action.hideFolderEdit ? null : state.selectedFolder,
      ui: {
        ...state.ui,
        isFolderEdit: action.hideFolderEdit ? false : state.ui.isFolderEdit,
        isDirty: true
      }
    };
  }),
  on(copActions.storeSavedFolderToStore, (state, action) => {
    const { mapState } = action;

    const maps = state.maps.slice();
    const index = maps.findIndex(m => m.uId === mapState.map.uId);
    if (index >= 0) {
      maps.splice(index, 1, ...[mapState.map]);
    }

    return {
      ...state,
      selectedFolder: null,
      maps,
      currentMap: mapState,
      ui: {
        ...state.ui,
        isFolderEdit: false,
        isFolderWorkflowSummaryConfigEdit: false,
        isDirty: true
      }
    };
  }),
  on(copActions.cancelDeleteFolderToStore, (state, action) => {
    return {
      ...state,
      selectedFolderForDelete: null
    };
  }),
  on(copActions.storeDeleteOfSelectedFolderToStore, (state, action) => {
    const { mapState } = action;

    const maps = state.maps.slice();
    const index = maps.findIndex(m => m.uId === mapState.map.uId);
    if (index >= 0) {
      maps.splice(index, 1, ...[mapState.map]);
    }

    return {
      ...state,
      maps,
      currentMap: mapState,
      selectedFolderForDelete: null,
      ui: {
        ...state.ui,
        isDirty: true
      }
    };
  }),
  on(copActions.storeNewLayerCanceledToStore, (state, action) => {
    const { mapState } = action;

    const maps = state.maps.slice();
    const index = maps.findIndex(m => m.uId === mapState.map.uId);
    if (index >= 0) {
      maps.splice(index, 1, ...[mapState.map]);
    }

    return {
      ...state,
      maps,
      currentMap: mapState,
      selectedLayer: null,
      ui: {
        ...state.ui,
        isLayerEdit: false,
        isLayerNew: false
      }
    };
  }),
  on(copActions.storeNewLayerAddedToStore, (state, action) => {
    const { layer, mapState } = action;

    const maps = state.maps.slice();
    const index = maps.findIndex(m => m.uId === mapState.map.uId);
    if (index >= 0) {
      maps.splice(index, 1, ...[mapState.map]);
    }

    return {
      ...state,
      selectedLayer: layer,
      selectedMapUId: mapState.map.uId,
      maps,
      currentMap: mapState,
      ui: {
        ...state.ui,
        isDirty: true,
        isLayerEdit: true,
        isLayerNew: true
      }
    };
  }),
  on(copActions.storeSelectedLayerForOptionsToStore, (state, action) => {
    return {
      ...state,
      selectedLayer: action.layer,
      ui: {
        ...state.ui,
        showLayerOptionsTab: action.showLayerOptionsTab
      }
    };
  }),
  on(copActions.storeEditedLayerToStore, (state, action) => {
    return {
      ...state,
      selectedLayer: action.layer,
      ui: {
        ...state.ui,
        isLayerEdit: true,
        isDirty: false,
        isLayerNew: false
      }
    };
  }),
  on(copActions.storeEditedLayerShowQuickFilterToStore, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isLayerEditQuickFilter: action.showQuickFilter
      }
    };
  }),
  on(copActions.storeCancelEditLayerToStore, (state, action) => {
    return {
      ...state,
      selectedLayer: null,
      ui: {
        ...state?.ui,
        isLayerEdit: false
      }
    };
  }),
  on(copActions.deleteLayerToStore, (state, action) => {
    return {
      ...state,
      selectedLayerForDelete: action.layer
    };
  }),
  on(copActions.cancelDeleteLayerToStore, (state, action) => {
    return {
      ...state,
      selectedLayerForDelete: null
    };
  }),
  on(copActions.storeSavedLayerToStore, (state, action) => {
    const { mapState } = action;

    const maps = state.maps.slice();
    const index = maps.findIndex(m => m.uId === mapState.map.uId);
    if (index >= 0) {
      maps.splice(index, 1, ...[mapState.map]);
    }

    return {
      ...state,
      maps,
      currentMap: mapState,
      selectedLayer: null,
      ui: {
        ...state.ui,
        isDirty: true,
        isLayerEdit: false,
        isLayerNew: false
      }
    };
  }),
  on(copActions.storeDeleteOfSelectedLayerToStore, (state, action) => {
    const { mapState } = action;

    const maps = state.maps.slice();
    const index = maps.findIndex(m => m.uId === mapState.map.uId);
    if (index >= 0) {
      maps.splice(index, 1, ...[mapState.map]);
    }

    return {
      ...state,
      maps,
      currentMap: mapState,
      selectedLayerForDelete: null,
      ui: {
        ...state.ui,
        isDirty: true,
        isLayerNew: false
      }
    };
  }),
  on(copActions.storeLoadedDatasetsForSelectionToStore, (state, action) => {
    return {
      ...state,
      datasetsForSelect: action.results?.data ?? []
    };
  }),
  on(copActions.expandFolderToStore, (state, action) => {
    const folderExpanded = produce(state.mapSettings.folderExpanded, draft => {
      draft[action.folder.uId] = true;
    });

    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        folderExpanded
      }
    };
  }),
  on(copActions.collapseFolderToStore, (state, action) => {
    const folderExpanded = produce(state.mapSettings.folderExpanded, draft => {
      draft[action.folder.uId] = false;
    });

    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        folderExpanded
      }
    };
  }),
  on(copActions.expandTropicalCycloneToStore, (state, action) => {
    const tropicalCycloneExpanded = produce(state.mapSettings.tropicalCycloneExpanded, draft => {
      draft[action.layerUId] = true;
    });

    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        tropicalCycloneExpanded
      }
    };
  }),
  on(copActions.collapseTropicalCycloneToStore, (state, action) => {
    const tropicalCycloneExpanded = produce(state.mapSettings.tropicalCycloneExpanded, draft => {
      draft[action.layerUId] = false;
    });

    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        tropicalCycloneExpanded
      }
    };
  }),
  on(copActions.setFolderVisibilityToHiddenToStore, (state, action) => {
    const { folder, containedFolders, containedLayers } = action;

    const mapSettings = produce(state.mapSettings, draft => {
      draft.folderVisibility[action.folder.uId] = FolderVisibility.hidden;

      containedFolders.forEach(f => (draft.folderVisibility[f.uId] = FolderVisibility.hidden));
      containedLayers.forEach(l => (draft.layerVisibility[l.uId] = false));
    });

    const mapState = produce(state.currentMap, draft => {
      Object.keys(draft.layerStates)
        .filter(uid => action.containedLayers.find(l => l.uId === uid))
        .forEach(uid => {
          if (!!draft.layerStates[uid]) {
            draft.layerStates[uid].isVisible = false;
          }
        });
      if (!!draft.folderStates[folder.uId]) {
        draft.folderStates[folder.uId].visibility = FolderVisibility.hidden;
      }
    });

    return {
      ...state,
      mapSettings,
      currentMap: mapState
    };
  }),
  on(copActions.setFolderVisibiltyToNotHiddenToStore, (state, action) => {
    const { folder, containedFolders, containedLayers } = action;

    const mapSettings = produce(state.mapSettings, draft => {
      draft.folderVisibility[action.folder.uId] = FolderVisibility.visible;

      containedFolders.forEach(f => (draft.folderVisibility[f.uId] = FolderVisibility.visible));
      containedLayers.forEach(l => (draft.layerVisibility[l.uId] = true));
    });

    const mapState = produce(state.currentMap, draft => {
      Object.keys(draft.layerStates)
        .filter(uid => action.containedLayers.find(l => l.uId === uid))
        .forEach(uid => {
          if (!!draft.layerStates[uid]) {
            draft.layerStates[uid].isVisible = true;
          }
        });
      if (!!draft.folderStates[folder.uId]) {
        draft.folderStates[folder.uId].visibility = FolderVisibility.visible;
      }
    });

    return {
      ...state,
      mapSettings,
      currentMap: mapState
    };
  }),
  on(copActions.setLayerOpacityToStore, (state, action) => {
    const layerOpacity = produce(state.mapSettings.layerOpacity, draft => {
      if (action.opacity > 0) {
        draft[action.layer.uId] = action.opacity;
      } else if (draft[action.layer.uId] >= 0) {
        delete draft[action.layer.uId];
      }
    });

    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        layerOpacity
      }
    };
  }),
  on(copActions.setLayerVisibilitiesToStore, (state, action) => {
    const layerVisibility = produce(state.mapSettings.layerVisibility, draft => {
      action.layers.forEach(layer => {
        draft[layer.uId] = action.showLayer;
      });
    });

    const mapState = produce(state.currentMap, draft => {
      Object.keys(draft.layerStates)
        .filter(uid => action.layers.find(l => l.uId === uid))
        .forEach(uid => {
          if (!!draft.layerStates[uid]) {
            draft.layerStates[uid].isVisible = action.showLayer;
          }
        });
    });

    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        layerVisibility
      },
      currentMap: mapState
    };
  }),
  on(copActions.openCloseLeftDrawerToStore, (state, action) => {
    const { isOpen } = action;

    return {
      ...state,
      ui: {
        ...state.ui
      },
      mapSettings: {
        ...state.mapSettings,
        isLeftDrawerOpen: isOpen
      }
    };
  }),
  /*  on(copActions.logout, () => {
    return {
      ...initialState
    };
  }),*/
  on(copActions.updateFolderVisibilityToStore, (state, action) => {
    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        folderVisibility: action.folderVisibility
      }
    };
  }),
  on(copActions.startWeatherAnimationToStore, (state, action) => {
    const currentMap = produce(state.currentMap, draft => {
      // for some reason action.name here is the folder.uid of weather layers
      const folderUId = action.name;
      draft.folderStates[folderUId].isAnimating = true;

      // this below is for old layer renderer
      for (let layer of draft.foldersSet[folderUId].layers) {
        draft.layerStates[layer.uId].isAnimated = true;
      }
    });

    return {
      ...state,
      currentMap
    };
  }),
  on(copActions.stopWeatherAnimationToStore, (state, action) => {
    const currentMap = produce(state.currentMap, draft => {
      // for some reason action.name here is the folder.uid of weather layers
      const folderUId = action.name;
      draft.folderStates[folderUId].isAnimating = false;

      for (let layer of draft.foldersSet[action.name].layers) {
        draft.layerStates[layer.uId].isAnimated = false;
      }
    });

    return {
      ...state,
      currentMap
    };
  }),
  on(copActions.setWorkflowStatusSummaryCompleteToStore, (state, action) => {
    return {
      ...state,
      workflowStatusSummary: action.summary
    };
  }),
  on(copActions.clearFloorValues, (state, action) => {
    return {
      ...state,
      floorValues: []
    };
  }),
  on(copActions.setEditMapWallToStore, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isMapWallEdit: action.isEdit
      }
    };
  }),
  on(copActions.showNightLayerToStore, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showNightLayer: true
      }
    };
  }),
  on(copActions.hideNightLayerToStore, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showNightLayer: false
      }
    };
  }),
  on(copActions.setMapSettingsBetaOptionsToStore, (state, action) => {
    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        betaOptions: action.betaOptions
      }
    };
  }),
  on(copActions.setLayerQuickFilterSelectionsToStore, (state, action) => {
    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        layerQuickFilterSelections: action.layerQuickFilterSelections
      }
    };
  }),
  on(copActions.setWorkflowStatusVisibilityByLayerTypeAndNameToStore, (state, action) => {
    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        workflowStatusVisibilityByLayerTypeAndName: action.workflowStatusVisibility
      },
      currentMap: {
        ...state.currentMap,
        workflowStatusVisibility: action.workflowStatusVisibility
      }
    };
  }),
  on(copActions.setWorkflowStatusExpandedByLayerTypeAndNameToStore, (state, action) => {
    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        workflowStatusExpandedByLayerTypeAndName: action.workflowStatusExpanded
      }
    };
  }),
  on(copActions.setSectionWidgetUserSettingsToStore, (state, action) => {
    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        sectionWidgetUserSettings: action.sectionWidgetUserSettings
      },
      currentMap: {
        ...state.currentMap,
        sectionWidgetUserSettings: action.sectionWidgetUserSettings
      }
    };
  }),
  on(copActions.setHiddenDocumentsByLayerToStore, (state, action) => {
    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        hiddenDocumentsByLayer: action.hiddenDocumentsByLayer
      },
      currentMap: {
        ...state.currentMap,
        hiddenDocumentsByLayer: action.hiddenDocumentsByLayer
      }
    };
  }),

  on(copActions.setGeoFieldsForOptionalDisplayToStore, (state, action) => {
    return {
      ...state,
      mapSettings: {
        ...state.mapSettings,
        geoFieldsForOptionalDisplay: action.geoFieldsForOptionalDisplay.filter(s => !!!(s as any).stepName)
      },
      currentMap: {
        ...state.currentMap,
        geoFieldsForOptionalDisplay: action.geoFieldsForOptionalDisplay.filter(s => !!!(s as any).stepName)
      }
    };
  }),
  on(copActions.setMapPositionSettingsToStore, (state, action) => {
    return {
      ...state,
      mapPosition: action.mapPosition
    };
  }),
  on(copActions.setOverviewModeToStore, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isOverviewMode: true
      }
    };
  }),
  on(copActions.setLinkDocumentToStore, (state, action) => {
    return {
      ...state,
      linkDocumentDatasetUId: action.datasetUId,
      linkDocumentUId: action.documentUId
    };
  }),
  on(copActions.processSignalRDatasetChangeEvent, (state, action) => {
    let datasets = { ...state.currentMap?.datasetsSet };
    let existingDataset = datasets[action.event.datasetUId];

    // Copy existing Workflow if any to the updated dataset
    // Workflow updates come separately from SignalR
    if (existingDataset) {
      let existingWorkflow = existingDataset?.workflow;

      let newDataset = {
        ...action.event.dataset,
        workflow: existingWorkflow
      };

      datasets[action.event.datasetUId] = newDataset;
    }

    return {
      ...state,
      currentMap: {
        ...state.currentMap,
        datasetsSet: datasets
      }
    };
  }),
  on(copActions.processSignalRWorkflowChangeEvent, (state, action) => {
    let datasets = { ...state.currentMap?.datasetsSet };
    let existingDataset = datasets[action.event.datasetUId];

    // Copy existing Workflow if any to the updated dataset
    // Workflow updates come separately from SignalR
    if (existingDataset) {
      let newDataset = {
        ...existingDataset,
        workflow: action.event.workflow
      };

      datasets[action.event.datasetUId] = newDataset;
    }

    return {
      ...state,
      currentMap: {
        ...state.currentMap,
        datasetsSet: datasets
      }
    };
  }),
  on(copActions.setFloorFilterComplete, (state, action) => {
    return {
      ...state,
      selectedFloor: action.floor
    };
  }),
  on(copActions.addDatasetToStore, (state, action) => {
    return produce(state, draft => {
      draft.currentMap.datasetsSet[action.dataset.uId] = action.dataset;
    });
  }),
  on(copActions.addGhostLayerToStore, (state, action) => {
    const { layerUId, datasetUId, document } = action;

    const dataset = state.currentMap.datasetsSet[datasetUId];
    const datasetName = dataset?.name;

    const changedState = produce(state.currentMap, draftState => {
      let existingLayer = draftState.layersSet[layerUId];
      if (!!existingLayer) {
        existingLayer.datasetUId = datasetUId;
        existingLayer.name = datasetName;
      } else {
        if (!draftState.map.folders[0].layers) {
          draftState.map.folders[0].layers = [];
        }
        const layer = {
          uId: layerUId,
          isActive: false,
          datasetUId,
          name: datasetName
        };

        draftState.map.folders[0].layers.push(layer);
        draftState.layersSet[layerUId] = layer;
        draftState.layerStates[layerUId] = {
          documentCount: 0,
          isVisible: true,
          isLoading: false,
          documents: {}
        };
      }
    });

    return {
      ...state,
      currentMap: changedState
    };
  }),
  on(copActions.setLayerVisibilitiesFromCurrentLayerState, (state, action) => {
    if (state.currentMap.layerStates) {
      const layerStates = state.currentMap.layerStates;
      const newLayerVisibility: { [key: string]: boolean } = {};
      Object.keys(layerStates).forEach(layerUId => {
        newLayerVisibility[layerUId] = layerStates[layerUId].isVisible || false;
      });

      return {
        ...state,
        mapSettings: {
          ...state.mapSettings,
          layerVisibility: newLayerVisibility
        }
      };
    }
  }),
  on(copActions.enableHistoryTrackForDocument, (state, { documentUId }) => ({
    ...state,
    historyTrackEnabledUIds: Array.from(new Set([...state.historyTrackEnabledUIds, documentUId]))
  })),
  on(copActions.disableHistoryTrackForDocument, (state, { documentUId }) => ({
    ...state,
    historyTrackEnabledUIds: state.historyTrackEnabledUIds.filter(id => id !== documentUId)
  }))
);

export const getCurrentMapState = (state: CopState) => state?.currentMap;
export const getMapLayers = (state: CopState) => state?.currentMap?.layers;
export const getMapWeatherFolders = ({ currentMap }: CopState) => {
  if (!currentMap) {
    return [];
  }

  const { folderStates = [], foldersSet = {}, layerStates } = currentMap;

  const weatherFolders = Object.values(foldersSet).reduce((acc, folder) => {
    if (folder.sectionType === SectionType.Weather) {
      const configuration = JSON.parse(folder?.configuration) ?? {};

      acc.push({
        uId: folder.uId,
        isAnimating: folderStates[folder.uId]?.isAnimating ?? false,
        configuration,
        layers: folder.layers.map(layer => ({
          uId: layer.uId,
          layerId: layer?.foreignDatasetDefinition?.name,
          isVisible: layerStates[layer.uId]?.isVisible
        }))
      });
    }

    return acc;
  }, [] as WeatherFolder[]);

  return weatherFolders;
};
export const getCurrentMapUId = (state: CopState) => state?.currentMap?.map?.uId;

export const getMaps = (state: CopState) => state?.maps;

export const getMapSettingsFromStorageToLoad = (state: CopState) => state?.mapSettingsFromStorageToLoad;
export const getPreviouslySelectedMapUId = (state: CopState) => state?.previouslySelectedMapUId;

export const getIsMapDirty = (state: CopState) => state?.ui.isDirty;
export const getIsMapEdit = (state: CopState) => state?.ui.isMapEdit;
export const getIsMapNew = (state: CopState) => state?.ui?.isMapNew;
export const getDatasetsForSelect = (state: CopState) => state?.datasetsForSelect;
export const getIsMapLoading = (state: CopState) => state?.isMapLoading;
export const getSelectedMapForDelete = (state: CopState) => state?.selectedMapUIdForDelete;

// LSD
export const getSelectedFolder = (state: CopState) => state?.selectedFolder;
export const getShowLayerOptionsTab = (state: CopState) => state?.ui?.showLayerOptionsTab;
export const getSelectedFolderForDelete = (state: CopState) => state?.selectedFolderForDelete;

export const getIsFolderEdit = (state: CopState) => state?.ui?.isFolderEdit;
export const getIsTrySaveMap = (state: CopState) => state?.ui?.isTrySaveMap;
export const getIsFolderWorkflowSummaryConfigEdit = (state: CopState) => state?.ui?.isFolderWorkflowSummaryConfigEdit;
export const getSelectedLayer = (state: CopState) => state?.selectedLayer;
export const getSelectedLayerForDelete = (state: CopState) => state?.selectedLayerForDelete;
export const getIsLayerEdit = (state: CopState) => state?.ui?.isLayerEdit;
export const getIsLayerEditQuickFilter = (state: CopState) => state?.ui?.isLayerEditQuickFilter;
export const getIsLayerNew = (state: CopState) => state?.ui?.isLayerNew;

//export const getCalculatedLayerVisibility = (state: CopState) => state?.selectedMapState?.calculatedLayerVisibility;
export const getFloorFilterOptions = (state: CopState) => state?.selectedMapState?.calculatedLayerFilterOptions;
export const getWorkflowStatusSummary = (state: CopState) => state.workflowStatusSummary;
export const getSelectedMapTileLayers = (state: CopState) => state?.selectedMapState?.mapTileLayers;
export const getMapPosition = (state: CopState) => state?.mapPosition;

// map settings
export const getMapSettings = (state: CopState) => state?.mapSettings;
export const getFolderExpanded = (state: CopState) => state?.mapSettings?.folderExpanded;
export const getLayerVisibility = (state: CopState) => state?.mapSettings?.layerVisibility;

//export const getLayerDocumentVisibility = (state: CopState) => state?.mapSettings?.layerDocumentVisibility;
export const getWorkflowStatusVisibilityByLayerTypeAndName = (state: CopState) =>
  state?.mapSettings?.workflowStatusVisibilityByLayerTypeAndName;
export const getWorkflowStatusExpandedByLayerTypeAndName = (state: CopState) =>
  state?.mapSettings?.workflowStatusExpandedByLayerTypeAndName;
export const getSectionWidgetUserSettings = (state: CopState) => state?.mapSettings?.sectionWidgetUserSettings;
export const getGeoFieldsForOptionalDisplay = (state: CopState) => state?.mapSettings?.geoFieldsForOptionalDisplay;
export const getFolderVisibility = (state: CopState) => state?.mapSettings?.folderVisibility;
export const getLayerOpacity = (state: CopState) => state?.mapSettings?.layerOpacity;
export const getBetaOptions = (state: CopState) => state?.mapSettings?.betaOptions;
export const getFloorFilterSetting = (state: CopState) => state?.mapSettings?.floorPlanFilter;
export const getMapTypeSettings = (state: CopState) => state?.mapSettings?.mapTypeSettings;
export const getMapType = (state: CopState) => state?.mapSettings?.mapTypeSettings?.selectedMapType;
export const isTransitEnabled = (state: CopState) => state?.mapSettings?.mapTypeSettings?.isTransit;
export const isTrafficEnabled = (state: CopState) => state?.mapSettings?.mapTypeSettings?.isTraffic;
export const isPOI = (state: CopState) => state?.mapSettings?.mapTypeSettings?.isPOI;
export const getLayerQuickFilterSelections = (state: CopState) => state.mapSettings.layerQuickFilterSelections;

export const getIsLeftDrawerOpen = (state: CopState) => state?.mapSettings?.isLeftDrawerOpen;
export const getTropicalCycloneProperties = (state: CopState) => state?.mapSettings?.tropicalCycloneExpanded;
export const getHiddenDocumentsByLayer = (state: CopState) => state?.mapSettings?.hiddenDocumentsByLayer;

// link document
export const getLinkDocumentUId = (state: CopState) => state?.linkDocumentUId;
export const getLinkDocumentDatasetUId = (state: CopState) => state?.linkDocumentDatasetUId;

// map room
export const getShowMapRoomDialog = (state: CopState) => state?.ui?.showMapRoomDialog;
export const getMapLinks = (state: CopState) => state?.mapLinks;
export const getMapLayerViews = (state: CopState) => state?.mapLayerViews;
export const getCollaboratorsForMapRoom = (state: CopState) => state?.mapRoomCollaborators;

// map
export const getDatasetsSet = (state: CopState) => state?.currentMap?.datasetsSet;
export const getSelectedDocuments = (state: CopState) => state?.currentMap?.selectedDocuments;

// floor picker
export const getFloorValues = (state: CopState) => state?.floorValues;
export const getSelectedFloor = (state: CopState) => state?.selectedFloor;

// wall
export const getIsMapWallEdit = (state: CopState) => state?.ui?.isMapWallEdit;
export const getShowNightLayer = (state: CopState) => state?.ui?.showNightLayer;
export const getIsOverviewMode = (state: CopState) => state?.ui?.isOverviewMode;

// history track
export const getHistoryTrackEnabledUIds = (state: CopState) => state?.historyTrackEnabledUIds ?? [];
